import type { ReactNode } from 'react'

import { useRecordTitle } from '@/shared/layout/page-title'
import { PageHeader } from '@/shared/layout/PageHeader'
import { SlotActions, type Icon, type SlotContext } from '@/shared/registry'

import { HeaderSearchField } from './HeaderSearchField'

export interface DetailsHeaderProps {
  readonly icon: Icon
  /** The record's type, such as "User"; left out on create and edit pages. */
  readonly eyebrow?: string
  readonly title: string
  /** A muted line under the title, such as the record's status or its parent. */
  readonly status?: ReactNode
  /** Secondary actions, such as Edit, as small buttons. */
  readonly actions?: ReactNode
  /** The search field, opening the matching search page. */
  readonly search?: { readonly to: string; readonly placeholder: string }
  /** An action slot whose registered actions follow the page's own (GW-19). */
  readonly actionSlot?: string
  readonly context?: SlotContext
  /** Changes when the record was changed, so the registered actions read it afresh. */
  readonly refreshKey?: number
  /**
   * The record's name for the browser tab's title, when the header shows it otherwise or names
   * more than one record, as a class's survey results name the survey and the class.
   */
  readonly recordName?: string | undefined
}

/**
 * The header every details, create and edit page starts with, from the mockups'
 * `details/DetailsHeader.jsx`: the icon tile, the record type, its title and status, and on the
 * right the page's actions, any registered ones, and the search field. With an eyebrow, the header
 * names a record, by its title or its eyebrow, which the browser tab's title then names too.
 */
export function DetailsHeader(props: DetailsHeaderProps) {
  const { search, actionSlot, recordName } = props
  const hasActions = props.actions !== undefined || actionSlot !== undefined || search !== undefined
  useRecordTitle(recordName ?? props.title, recordName ?? props.eyebrow)

  return (
    <PageHeader
      icon={props.icon}
      title={props.title}
      {...(props.eyebrow === undefined ? {} : { eyebrow: props.eyebrow })}
      {...(props.status === undefined ? {} : { subtitle: props.status })}
      {...(hasActions
        ? {
            actions: (
              <div className="flex flex-wrap items-center gap-2">
                {props.actions}
                {actionSlot !== undefined && <SlotActions key={props.refreshKey} slot={actionSlot} context={props.context} className="gap-2" />}
                {search !== undefined && <HeaderSearchField to={search.to} placeholder={search.placeholder} />}
              </div>
            ),
          }
        : {})}
    />
  )
}
